import { IHttpService, IPromise } from "angular";
import { IcdOfficeModel, IcdOfficeCreateDTO, IcdOfficeUpdateDTO, IcdOfficeListResponse, IcdCorporationStatusEnum, ICDOfficeDivisionModel, CKERetailToggleModel } from "@herbjoy/apex-api-models";

interface IcdOfficeGetRequest {
    icdOfficeID?: number;
    officeName?: string;
    inactive?: boolean;
    icdCorporationID?: number;
    icdCorporationName?: string;
    icdCorporationStatus?: IcdCorporationStatusEnum;
    officeCity?: string;
    officeState?: string;
    moveInDate?: Date;
    moveOutDate?: Date;
    moduleAdminID?: number;
    moduleID?: number;
    addedAfter?: number;
    modifiedSince?: Date;
    divisionID?: number;
    employeeID?: number;
    employmentDate?: number;
}

export interface IcdOfficeResponse extends Omit<IcdOfficeModel, "OfficeDivisions"> {
    OfficeId?: number;
    OfficeDivisions: {
        Divisions: { [divisionID: number]: ICDOfficeDivisionModel };
    };
}

export class IcdOfficeService {
    constructor(private $http: IHttpService, private apexApiUrl: string) {
        'ngInject'
    }

    triggerTelemapperSync(meridianOfficeID: number) {
        var data = {
            "teleMapperEntryID": null,
            "meridianOfficeID": meridianOfficeID
        }
        return this.$http({
            method: "POST",
            headers: { Accept: "application/json" },
            url: this.apexApiUrl + "payrolls/create-meridian-job",
            params: data
        }).then(function (response) {
            return response.data;
        });
    }

    triggerCKESync(meridianOfficeID: number) {
        var data = {
            "teleMapperEntryID": null,
            "meridianOfficeID": meridianOfficeID
        }
        return this.$http({
            method: "POST",
            headers: { Accept: "application/json" },
            url: this.apexApiUrl + "payrolls/create-cke-job",
            params: data
        }).then(function (response) {
            return response.data;
        });
    }

    /**
     * Get all ICD offices matching the given query requirements.
     * @param query The filter parameters for the office list.
     * @returns The resolved list of ICD offices
     */

    retrieveIcdOffices(query: IcdOfficeGetRequest = null) {
        return this.$http<IcdOfficeListResponse[]>({
            method: "GET",
            headers: { Accept: "application/json" },
            url: this.apexApiUrl + "icd-offices",
            params: query
        }).then(function (response) {
            return response.data;
        });
    }

    retrieveCKERetailInfo() {
        return this.$http<CKERetailToggleModel[]>({
            method: "GET",
            headers: { Accept: "application/json" },
            url: this.apexApiUrl + "icd-offices/getCKEToggleInfo",
        }).then(function (response) {
            return response.data;
        });
    }

    updateCKERetailInfo(data: CKERetailToggleModel): IPromise<boolean> {
        return this.$http({
            method: "POST",
            url: this.apexApiUrl + "icd-offices/setCKEToggleInfo",
            data: data,
            headers: { "Content-Type": "application/json" }
        }).then((dt) => {
            console.log(dt);
            return true
        }).catch((err) => {
            console.log(err);
            return false;
        });
    }

    //updateCKERetailInfo(data: CKERetailToggleModelResponse): IPromise<void> {
    //    return this.$http({
    //        method: "PUT",
    //        url: this.apexApiUrl + "icd-offices/setCKEToggleInfo",
    //        data: data,
    //        headers: { "Content-Type": "application/json" }
    //    }).then(() => null);
    //}

    retrieveIcdOffice(icdOfficeID: number) {
        return this.$http<IcdOfficeResponse>({
            method: "GET",
            url: this.apexApiUrl + "icd-offices/" + icdOfficeID,
            headers: { Accept: "application/json " }
        }).then(function (response) {
            return response.data;
        })
    }

    createIcdOffice(office: IcdOfficeCreateDTO) {
        return this.$http<{ Id: number }>({
            method: "POST",
            url: this.apexApiUrl + "icd-offices",
            data: office,
            headers: { Accept: "application/json", "Content-Type": "application/json" }
        }).then(function (response) {
            return response.data;
        })
    }

    updateIcdOffice(icdOfficeID: number, office: IcdOfficeUpdateDTO): IPromise<void> {
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "icd-offices/" + icdOfficeID,
            data: office,
            headers: { "Content-Type": "application/json" }
        }).then(() => null);
    }

    /**
     * Create a roster sync job for the given office. The roster sync job will
     * perform an import of the office's roster from third-part sources, where available.
     * @param {number} icdOfficeID The office whose roster will be synced
     * @param {Date} lastDate The cutoff date for updates to the roster.
     */
    createIcdOfficeRosterSyncJob(icdOfficeID: number, lastDate: Date): IPromise<void> {
        return this.$http({
            method: "POST",
            url: `${this.apexApiUrl}/icd-offices/${icdOfficeID}/roster-sync-job`,
            data: {
                lastDate
            }
        }).then(() => null);
    }

    /**
     * Move the Meridian Office ID from the given office to a new office.
     * @param icdOfficeID The office currently owning the Meridan Office ID
     * @param request The details of the request, including the new target office.
     */
    moveMeridianOfficeID(icdOfficeID: number, request: { icdOfficeID: number }) {
        return this.$http<void>({
            method: "POST",
            url: `${this.apexApiUrl}/icd-offices/${icdOfficeID}/meridian-office-id/movement`,
            data: request
        }).then(response => response.data);
    }

    deleteIcdOffice(icdOfficeID: number): IPromise<void> {
        return this.$http({
            method: "DELETE",
            url: this.apexApiUrl + "icd-offices/" + icdOfficeID
        }).then(() => null);
    }
}

export function sortIcdOfficeByOwner(a, b) {
    if (a.OwnerName.toLowerCase() < b.OwnerName.toLowerCase()) {
        return -1;
    } else if (a.OwnerName.toLowerCase() > b.OwnerName.toLowerCase()) {
        return 1;
    }
    if (a.IcdCorporationName.toLowerCase() < b.IcdCorporationName.toLowerCase()) {
        return -1;
    } else if (a.IcdCorporationName.toLowerCase() < b.IcdCorporationName.toLowerCase()) {
        return 1;
    }

    if (a.OfficeName.toLowerCase() < b.OfficeName.toLowerCase()) {
        return -1;
    } else if (a.OfficeName.toLowerCase() > b.OfficeName.toLowerCase()) {
        return 1;
    }

    return 0;
}
